import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "../api/queryClient";
import type { OptionCategoryType } from "../api/optionCategories";
import type { NavigationItemType } from "../root/layout/SidebarMenuItem";
import { usePermission } from "../shared/permissions";
import { usePrivacyNavigate } from "../shared/usePrivacyNavigate";
import { OptionCategoryHistory } from "./OptionCategoryHistory";
import { OptionCategoryListPage } from "./OptionCategoryListPage";
import { toOptionCategoryListItem } from "./optionCategoryMapping";
import type { OptionCategoryScope } from "./optionCategoryScopes";
import { OPTION_CATEGORY_TYPE_CONFIG, optionCategoryTextKeys } from "./optionCategoryTypeConfig";

type OptionCategoryPageProps = { scope: OptionCategoryScope; categoryType: OptionCategoryType };

const OptionCategoryPage = ({ scope, categoryType }: OptionCategoryPageProps) => {
  const config = OPTION_CATEGORY_TYPE_CONFIG[categoryType];
  const { hasPermission } = usePermission();
  const { paths } = usePrivacyNavigate();
  const listQuery = useQuery(scope.listQueryOptions(categoryType));
  const createMutation = useMutation(scope.createMutationOptions());
  const updateMutation = useMutation(scope.updateMutationOptions());
  const deleteMutation = useMutation(scope.deleteMutationOptions());

  return (
    <OptionCategoryListPage
      textKeys={optionCategoryTextKeys(categoryType, scope.name)}
      hasDescription={scope.hasDescription(categoryType)}
      usageLinkTo={scope.usageLinkTo?.(paths, categoryType)}
      items={listQuery.data?.options.map(option => toOptionCategoryListItem(option, config.usageFormTypes))}
      canCreate={hasPermission(scope.permission(categoryType, "create"))}
      canUpdate={hasPermission(scope.permission(categoryType, "update"))}
      canDelete={hasPermission(scope.permission(categoryType, "delete"))}
      onCreate={values => createMutation.mutateAsync({ categoryType, values })}
      onUpdate={(id, values) => updateMutation.mutateAsync({ categoryType, id, values })}
      onDelete={id => deleteMutation.mutateAsync({ categoryType, id })}
      historyTab={{
        listTabKey: config.listTabKey,
        content: <OptionCategoryPageHistory scope={scope} categoryType={categoryType} />,
        onHover: () => queryClient.prefetchQuery(scope.historyQueryOptions(categoryType)),
      }}
    />
  );
};

const OptionCategoryPageHistory = ({ scope, categoryType }: OptionCategoryPageProps) => {
  const historyQuery = useQuery(scope.historyQueryOptions(categoryType));

  return (
    <OptionCategoryHistory
      entries={historyQuery.data?.history}
      nameHeaderKey={OPTION_CATEGORY_TYPE_CONFIG[categoryType].historyNameKey}
    />
  );
};

export const optionCategoryPageRoute = (
  scope: OptionCategoryScope,
  categoryType: OptionCategoryType
): NavigationItemType["route"] => ({
  path: scope.routePath(categoryType),
  element: <OptionCategoryPage key={categoryType} scope={scope} categoryType={categoryType} />,
});
